export type CloudinaryTransformOptions = {
  width?: number;
  height?: number;
  crop?: 'fill' | 'fit' | 'limit' | 'scale' | 'thumb' | 'pad' | 'lfill' | 'mfit';
  quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low' | `${number}`;
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'jpeg' | 'png' | 'gif' | string;
  dpr?: 'auto' | `${number}`;
  gravity?: 'auto' | 'center' | 'faces' | string;
};

export function isCloudinaryUrl(url: string): boolean {
  try {
    return /res\.cloudinary\.com\/[^/]+\/image\/upload\//.test(url);
  } catch {
    return false;
  }
}

export function getCloudinaryPreviewUrl(url: string, quality: number = 90): string {
  if (!url || !isCloudinaryUrl(url)) return url;

  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;

  const before = url.slice(0, idx + marker.length);
  const after = url.slice(idx + marker.length);

  const q = `q_${Math.max(1, Math.min(100, Math.round(quality)))}`;

  if (after.startsWith(`${q}/`)) return url;
  if (after.startsWith('q_') && after.includes('/')) return url;
  if (after.includes(`/${q}/`) || after.startsWith(`${q},`)) return url;

  return `${before}${q}/${after}`;
}

export function getOptimizedCloudinaryUrl(url: string, opts: CloudinaryTransformOptions = {}): string {
  if (!url || !isCloudinaryUrl(url)) return url;

  const parts: string[] = [];
  const quality = opts.quality ?? 'auto';
  const format = opts.format ?? 'auto';
  const dpr = opts.dpr ?? 'auto';

  if (format) parts.push(`f_${format}`);
  if (quality) parts.push(`q_${quality}`);
  if (dpr) parts.push(`dpr_${dpr}`);

  const hasW = typeof opts.width === 'number' && opts.width > 0;
  const hasH = typeof opts.height === 'number' && opts.height > 0;

  if (hasW) parts.push(`w_${opts.width}`);
  if (hasH) parts.push(`h_${opts.height}`);

  if (hasW || hasH) {
    const crop = opts.crop ?? (hasW && hasH ? 'fill' : 'limit');
    parts.push(`c_${crop}`);
    if (crop === 'fill') {
      const gravity = opts.gravity ?? 'auto';
      parts.push(`g_${gravity}`);
    }
  }

  const transform = parts.join(',');
  if (!transform) return url;

  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const before = url.slice(0, idx + marker.length);
  const after = url.slice(idx + marker.length);

  // Avoid duplicating transforms if already present
  if (after.startsWith('v') || after.startsWith('http') || after.startsWith('https')) {
    return `${before}${transform}/${after}`;
  }
  // If there are existing transforms, prepend ours for stronger control
  return `${before}${transform}/${after}`;
}
